<script lang="ts">
	import { changeLanguage, currentLanguage, languages } from '$lib/stores/i18n';
	import { onMount } from 'svelte';

	let isOpen = false;
	let mounted = false;

	onMount(() => {
		mounted = true;
	});

	function toggleDropdown() {
		isOpen = !isOpen;
	}

	function closeDropdown() {
		isOpen = false;
	}

	async function handleLanguageChange(lang: string) {
		await changeLanguage(lang as 'en' | 'th');
		closeDropdown();
	}

	// Close dropdown when clicking outside
	function handleClickOutside(event: MouseEvent) {
		const target = event.target as HTMLElement;
		if (!target.closest('.language-switcher')) {
			closeDropdown();
		}
	}

	onMount(() => {
		document.addEventListener('click', handleClickOutside);
		return () => {
			document.removeEventListener('click', handleClickOutside);
		};
	});
</script>

<div class="language-switcher">
	<button
		class="language-btn"
		on:click={toggleDropdown}
		aria-label="Change language"
		aria-expanded={isOpen}
		type="button"
	>
		<span class="language-code">{$currentLanguage.toUpperCase()}</span>
		<i class="fas fa-chevron-down" class:rotated={isOpen}></i>
	</button>

	{#if isOpen}
		<div class="language-dropdown">
			{#each languages as lang}
				<button
					class="language-option"
					class:active={$currentLanguage === lang.code}
					on:click={() => handleLanguageChange(lang.code)}
					type="button"
				>
					<span class="language-name">{lang.nativeName}</span>
					{#if $currentLanguage === lang.code}
						<i class="fas fa-check"></i>
					{/if}
				</button>
			{/each}
		</div>
	{/if}
</div>

<style>
	.language-switcher {
		position: relative;
		display: inline-block;
	}

	.language-btn {
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 8px 12px;
		background: rgba(255, 255, 255, 0.1);
		border: 1px solid rgba(255, 255, 255, 0.2);
		border-radius: 6px;
		color: #fff;
		cursor: pointer;
		font-size: 14px;
		font-weight: 500;
		transition: all 0.3s ease;
		backdrop-filter: blur(10px);
	}

	.language-btn:hover {
		background: rgba(255, 255, 255, 0.15);
		border-color: rgba(255, 255, 255, 0.3);
	}

	.language-code {
		font-weight: 600;
		min-width: 30px;
		text-align: center;
	}

	.language-btn i {
		font-size: 10px;
		transition: transform 0.3s ease;
	}

	.language-btn i.rotated {
		transform: rotate(180deg);
	}

	.language-dropdown {
		position: absolute;
		top: calc(100% + 8px);
		right: 0;
		background: #1a1a1a;
		border: 1px solid rgba(255, 255, 255, 0.1);
		border-radius: 8px;
		min-width: 150px;
		box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
		z-index: 1000;
		overflow: hidden;
		animation: slideDown 0.2s ease;
	}

	@keyframes slideDown {
		from {
			opacity: 0;
			transform: translateY(-10px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	.language-option {
		display: flex;
		align-items: center;
		justify-content: space-between;
		width: 100%;
		padding: 12px 16px;
		background: transparent;
		border: none;
		color: #fff;
		cursor: pointer;
		font-size: 14px;
		transition: background 0.2s ease;
		text-align: left;
	}

	.language-option:hover {
		background: rgba(255, 255, 255, 0.1);
	}

	.language-option.active {
		background: rgba(74, 108, 247, 0.2);
		color: #4a6cf7;
	}

	.language-option.active:hover {
		background: rgba(74, 108, 247, 0.3);
	}

	.language-name {
		flex: 1;
	}

	.language-option i {
		font-size: 12px;
		margin-left: 8px;
	}

	/* Mobile styles */
	@media (max-width: 768px) {
		.language-btn {
			padding: 6px 10px;
			font-size: 13px;
		}

		.language-dropdown {
			right: auto;
			left: 0;
		}
	}
</style>
